/**
 * 認証の状態機械。React に依存しない素のクラスにしてある。
 *
 * React から切り離した理由は 2 つある。urql のクライアント（React の外で作る）から
 * トークンと 401 の扱いへ同じ口で触れること、そして状態遷移を画面を立てずに
 * テストできることである。React 側は AuthProvider が useSyncExternalStore で薄く包む。
 *
 * 状態:
 *   initializing   起動直後。黙って取り直せるかを確かめている
 *   unauthenticated ログイン画面を出す
 *   authorizing    認可サーバーへ飛ばした / コールバックを処理している
 *   authenticated  アクセストークンをメモリに持っている
 *   error          認可に失敗した。理由を画面に出して再試行させる
 */

import type { AdminAuthConfig } from "./config";
import {
  clearPendingAuthorization,
  createAuthorizationRequest,
  discoverEndpoints,
  exchangeAuthorizationCode,
  OAuthError,
  readCallbackParams,
  refreshAccessToken,
  savePendingAuthorization,
  takePendingAuthorization,
  type AuthorizationServerEndpoints,
} from "./oauth";
import {
  clearCachedClientId,
  readCachedClientId,
  registerClient,
  registrationCacheKey,
  writeCachedClientId,
} from "./registration";
import { InMemoryTokenStore } from "./token-store";

export type AuthStatus =
  | "initializing"
  | "unauthenticated"
  | "authorizing"
  | "authenticated"
  | "error";

export type AuthState = {
  status: AuthStatus;
  /** 失敗の理由。画面にそのまま出す（トークンや code は載せない） */
  error: string | null;
  /** 認可のやり直しで指定したテナント。応答が別テナントなら画面が気づける */
  requestedTenantId: string | null;
};

/** 「直前までログインしていた」だけを覚える印。トークンではない */
const HAD_SESSION_KEY = "visumo.admin.oauth.had-session";
const CONTEXT_HINT_KEY = "visumo.admin.oauth.context-hint";
const RESTORE_ATTEMPTED_KEY = "visumo.admin.oauth.restore-attempted";
const TENANT_HINT_KEY = "visumo.admin.oauth.tenant-hint";

export type AdminSessionDeps = {
  config: AdminAuthConfig;
  fetchImpl?: typeof fetch;
  /** ブラウザを認可サーバーへ飛ばす。テストで差し替える */
  navigate?: (url: string) => void;
  now?: () => number;
};

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

type BrowserSessionHint = {
  tenantId: string | null;
  delegationId: string | null;
  expiresAt: number | null;
};
function storageHint(
  storage: Storage | null,
  key: string,
): BrowserSessionHint | null {
  try {
    const value: unknown = JSON.parse(storage?.getItem(key) ?? "null");
    if (
      typeof value !== "object" ||
      value === null ||
      !("tenantId" in value) ||
      !("delegationId" in value) ||
      !("expiresAt" in value)
    )
      return null;
    if (
      (value.tenantId !== null && typeof value.tenantId !== "string") ||
      (value.delegationId !== null && typeof value.delegationId !== "string") ||
      (value.expiresAt !== null && typeof value.expiresAt !== "number")
    )
      return null;
    return value as BrowserSessionHint;
  } catch {
    return null;
  }
}
function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
function delegatedId(accessToken: string): string | null {
  try {
    const encoded = accessToken
      .split(".")[1]
      ?.replace(/-/g, "+")
      .replace(/_/g, "/");
    if (!encoded) return null;
    const claims = JSON.parse(atob(encoded)) as {
      act?: { delegation_id?: unknown };
    };
    return typeof claims.act?.delegation_id === "string"
      ? claims.act.delegation_id
      : null;
  } catch {
    return null;
  }
}

function describe(error: unknown): string {
  if (error instanceof OAuthError) {
    return `${error.message}（${error.code}）`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/**
 * 認可サーバーが選んだテナントを、タブ単位の次回リクエストのヒントにする。
 * 署名検証や認可の代わりには使わない。復元時はサーバーが所属と cookie を再検証する。
 */
function tenantHint(accessToken: string): string | null {
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
    const claims: unknown = JSON.parse(
      new TextDecoder().decode(
        Uint8Array.from(decoded, (char) => char.charCodeAt(0)),
      ),
    );
    if (
      typeof claims !== "object" ||
      claims === null ||
      !("tenant_id" in claims)
    )
      return null;
    return typeof claims.tenant_id === "string" && claims.tenant_id.length > 0
      ? claims.tenant_id
      : null;
  } catch {
    return null;
  }
}

export class AdminSession {
  readonly tokens = new InMemoryTokenStore();

  #state: AuthState = {
    status: "initializing",
    error: null,
    requestedTenantId: null,
  };
  #listeners = new Set<() => void>();
  #endpoints: AuthorizationServerEndpoints | null = null;
  #clientId: string | null = null;
  #config: AdminAuthConfig;
  #fetch: typeof fetch;
  #navigate: (url: string) => void;
  #now: () => number;
  #initializing: Promise<void> | null = null;
  #refreshing: Promise<string | null> | null = null;
  #epoch = 0;
  #delegated = false;

  constructor(deps: AdminSessionDeps) {
    this.#config = deps.config;
    const fetchImpl =
      deps.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
    this.#fetch = (input, init) =>
      fetchImpl(input, {
        ...init,
        signal: init?.signal ?? AbortSignal.timeout(10_000),
      });
    this.#navigate =
      deps.navigate ?? ((url: string) => window.location.assign(url));
    this.#now = deps.now ?? (() => Date.now());
  }

  get config(): AdminAuthConfig {
    return this.#config;
  }

  snapshot(): AuthState {
    return this.#state;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  #set(next: Partial<AuthState>): void {
    this.#state = { ...this.#state, ...next };
    for (const listener of this.#listeners) {
      listener();
    }
  }

  #redirectUri(): string {
    return new URL(
      this.#config.redirectPath,
      window.location.origin,
    ).toString();
  }

  async #resolveEndpoints(): Promise<AuthorizationServerEndpoints> {
    if (this.#endpoints !== null) {
      return this.#endpoints;
    }
    // 明示指定があればメタデータを引かない（サーバーが未対応でも繋がるようにする）
    const explicitAuthorization = this.#config.authorizationEndpoint;
    const explicitToken = this.#config.tokenEndpoint;
    if (explicitAuthorization !== null && explicitToken !== null) {
      const explicit: AuthorizationServerEndpoints = {
        authorizationEndpoint: explicitAuthorization,
        tokenEndpoint: explicitToken,
        registrationEndpoint: this.#config.registrationEndpoint,
      };
      this.#endpoints = explicit;
      return explicit;
    }
    const discovered = await discoverEndpoints(
      this.#config.issuer,
      this.#fetch,
    );
    const resolved: AuthorizationServerEndpoints = {
      authorizationEndpoint:
        explicitAuthorization ?? discovered.authorizationEndpoint,
      tokenEndpoint: explicitToken ?? discovered.tokenEndpoint,
      registrationEndpoint:
        this.#config.registrationEndpoint ?? discovered.registrationEndpoint,
    };
    this.#endpoints = resolved;
    return resolved;
  }

  #registrationCacheKey(): string {
    return registrationCacheKey({
      issuer: this.#config.issuer,
      redirectUri: this.#redirectUri(),
      scope: this.#config.scope,
    });
  }

  #browserHintKey(): string | null {
    const clientId =
      this.#config.clientId ??
      this.#clientId ??
      readCachedClientId(this.#registrationCacheKey());
    return clientId === null
      ? null
      : `visumo.admin.oauth.browser-session:${this.#registrationCacheKey()}:${clientId}`;
  }

  #forgetBrowserHint(): void {
    const key = this.#browserHintKey();
    if (key) safeLocalStorage()?.removeItem(key);
    safeSessionStorage()?.removeItem(CONTEXT_HINT_KEY);
  }

  #rememberBrowserHint(hint: BrowserSessionHint): void {
    safeSessionStorage()?.setItem(CONTEXT_HINT_KEY, JSON.stringify(hint));
    safeSessionStorage()?.removeItem(RESTORE_ATTEMPTED_KEY);
    const key = this.#browserHintKey();
    if (key) safeLocalStorage()?.setItem(key, JSON.stringify(hint));
  }

  #submitForm(endpoint: string, fields: Record<string, string>): void {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = endpoint;
    form.style.display = "none";
    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    try {
      form.submit();
    } finally {
      form.remove();
    }
  }

  /**
   * この画面が名乗る client_id。
   *
   * 設定に固定値があればそれを使い、無ければ動的クライアント登録（RFC 7591）で
   * 取りに行く。発行された client_id は localStorage に残して使い回す
   * （秘密ではないので置いてよい。トークンは別で、絶対に置かない）。
   */
  async #resolveClientId(options?: {
    forceRegister?: boolean;
  }): Promise<string> {
    const fixed = this.#config.clientId;
    if (fixed !== null) {
      return fixed;
    }
    const cacheKey = this.#registrationCacheKey();
    if (options?.forceRegister === true) {
      this.#clientId = null;
      clearCachedClientId(cacheKey);
    } else {
      if (this.#clientId !== null) {
        return this.#clientId;
      }
      const cached = readCachedClientId(cacheKey);
      if (cached !== null) {
        this.#clientId = cached;
        return cached;
      }
    }
    const endpoints = await this.#resolveEndpoints();
    const registrationEndpoint =
      endpoints.registrationEndpoint ?? `${this.#config.issuer}/register`;
    const clientId = await registerClient({
      registrationEndpoint,
      clientName: this.#config.clientName,
      redirectUris: [this.#redirectUri()],
      scope: this.#config.scope,
      fetchImpl: this.#fetch,
    });
    this.#clientId = clientId;
    writeCachedClientId(cacheKey, clientId);
    return clientId;
  }

  /**
   * 登録が失効していたら 1 回だけ登録し直して同じことをやり直す。
   * 2 回目も `invalid_client` なら諦めて理由を画面へ出す。
   */
  async #withClientId<T>(run: (clientId: string) => Promise<T>): Promise<T> {
    const clientId = await this.#resolveClientId();
    try {
      return await run(clientId);
    } catch (error) {
      const invalidClient =
        error instanceof OAuthError && error.code === "invalid_client";
      if (!invalidClient || this.#config.clientId !== null) {
        throw error;
      }
      const reissued = await this.#resolveClientId({ forceRegister: true });
      return run(reissued);
    }
  }

  /**
   * 起動時。リロードでトークンは消えているので、
   * 黙って取り直せる設定なら試し、無理ならログイン画面へ戻す。
   *
   * **初期状態のときだけ動く。** `/auth/callback` では画面側の効果が先に走って
   * `completeAuthorization` が状態を決めているので、そこへ後から
   * 「まだログインしていない」を上書きすると、**認可サーバーが返した拒否の理由が
   * 消える**。オペレーター向けアプリで `access_denied` が返ったときに、理由が
   * 出ないまま「ログインしています」で止まっていたのがこれである。
   */
  initialize(): Promise<void> {
    if (this.#initializing) return this.#initializing;
    if (this.#state.status !== "initializing") return Promise.resolve();
    this.#initializing = this.#initialize();
    return this.#initializing;
  }

  async #initialize(): Promise<void> {
    if (window.location.pathname === "/delegation/start") return;
    const hadSession = safeSessionStorage()?.getItem(HAD_SESSION_KEY) === "1";
    const key = this.#browserHintKey();
    const hint =
      storageHint(safeSessionStorage(), CONTEXT_HINT_KEY) ??
      (key ? storageHint(safeLocalStorage(), key) : null);
    if (
      !this.#config.silentAuth ||
      (!hadSession && !hint) ||
      safeSessionStorage()?.getItem(RESTORE_ATTEMPTED_KEY) === "1" ||
      (hint?.expiresAt != null && hint.expiresAt <= this.#now())
    ) {
      this.#set({ status: "unauthenticated", error: null });
      return;
    }
    safeSessionStorage()?.removeItem(HAD_SESSION_KEY);
    safeSessionStorage()?.setItem(RESTORE_ATTEMPTED_KEY, "1");
    await this.login({
      tenantId:
        safeSessionStorage()?.getItem(TENANT_HINT_KEY) ??
        hint?.tenantId ??
        null,
      returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      silent: true,
      expectedDelegationId: hint?.delegationId ?? null,
    });
  }

  /** 認可を開始する。ブラウザごと認可サーバーへ出ていく */
  async login(options?: {
    tenantId?: string | null;
    returnTo?: string;
    silent?: boolean;
    expectedDelegationId?: string | null;
  }): Promise<void> {
    const epoch = ++this.#epoch;
    this.#delegated = false;
    const tenantId = options?.tenantId ?? null;
    this.#set({
      status: "authorizing",
      error: null,
      requestedTenantId: tenantId,
    });
    try {
      const endpoints = await this.#resolveEndpoints();
      const clientId = await this.#resolveClientId();
      const request = await createAuthorizationRequest({
        endpoints,
        clientId,
        scope: this.#config.scope,
        redirectUri: this.#redirectUri(),
        resource: this.#config.resource,
        returnTo:
          options?.returnTo ??
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        ...(options?.silent ? { prompt: "none" as const } : {}),
        tenantId,
        tenantParam: this.#config.tenantParam,
      });
      if (epoch !== this.#epoch) return;
      const url = new URL(request.url);
      if (options?.silent) {
        url.searchParams.set(
          "session_mode",
          options.expectedDelegationId ? "delegated" : "normal",
        );
        if (options.expectedDelegationId)
          url.searchParams.set("delegation_id", options.expectedDelegationId);
      }
      savePendingAuthorization({
        ...request.pending,
        ...(options?.silent
          ? { expectedDelegationId: options.expectedDelegationId ?? null }
          : {}),
      });
      this.#navigate(url.toString());
    } catch (error) {
      if (epoch !== this.#epoch) return;
      clearPendingAuthorization();
      this.#set({
        status: options?.silent ? "unauthenticated" : "error",
        error: options?.silent ? null : describe(error),
      });
    }
  }

  /**
   * 認可サーバーからの戻りを処理する。`/auth/callback` の画面から呼ぶ。
   * 戻り先のパスを返す（呼び出し側がそこへ遷移する）。
   */
  async completeAuthorization(search: string): Promise<string | null> {
    const epoch = ++this.#epoch;
    const params = readCallbackParams(search);
    const pending = takePendingAuthorization();
    if (params.kind === "error") {
      if (pending?.silent) {
        safeSessionStorage()?.removeItem(HAD_SESSION_KEY);
        this.#set({ status: "unauthenticated", error: null });
        return pending.returnTo;
      }
      this.#set({
        status: "error",
        error: `認可が拒否されました（${params.error}${
          params.description === null ? "" : `: ${params.description}`
        }）`,
      });
      return null;
    }
    if (params.kind === "none") {
      this.#set({ status: "unauthenticated", error: null });
      return null;
    }
    if (pending === null) {
      this.#set({
        status: "error",
        error: "認可の途中経過が見つかりません。もう一度ログインしてください。",
      });
      return null;
    }
    if (pending.state !== params.state) {
      // state の不一致は CSRF の疑い。黙って続けない
      this.#set({
        status: "error",
        error:
          "認可応答の state が一致しません。もう一度ログインしてください。",
      });
      return null;
    }
    this.#set({ status: "authorizing", error: null });
    try {
      const endpoints = await this.#resolveEndpoints();
      const tokens = await this.#withClientId((clientId) =>
        exchangeAuthorizationCode({
          tokenEndpoint: endpoints.tokenEndpoint,
          clientId,
          code: params.code,
          codeVerifier: pending.codeVerifier,
          redirectUri: pending.redirectUri,
          resource: this.#config.resource,
          fetchImpl: this.#fetch,
          now: this.#now(),
        }),
      );
      if (epoch !== this.#epoch) return null;
      const delegationId = delegatedId(tokens.accessToken);
      if (
        pending.expectedDelegationId !== undefined &&
        delegationId !== pending.expectedDelegationId
      )
        throw new Error(
          "復元されたセッションの代行状態が一致しません。代行を開始し直してください。",
        );
      this.#delegated = delegationId !== null;
      this.tokens.set(
        this.#delegated ? { ...tokens, refreshToken: null } : tokens,
      );
      safeSessionStorage()?.setItem(HAD_SESSION_KEY, "1");
      const selectedTenant = tenantHint(tokens.accessToken) ?? pending.tenantId;
      if (selectedTenant)
        safeSessionStorage()?.setItem(TENANT_HINT_KEY, selectedTenant);
      else safeSessionStorage()?.removeItem(TENANT_HINT_KEY);
      this.#rememberBrowserHint({
        tenantId: selectedTenant,
        delegationId,
        expiresAt: delegationId ? tokens.expiresAt : null,
      });
      this.#set({
        status: "authenticated",
        error: null,
        requestedTenantId: pending.tenantId,
      });
      return pending.returnTo;
    } catch (error) {
      if (epoch !== this.#epoch) return null;
      this.tokens.clear();
      this.#set({ status: "error", error: describe(error) });
      return null;
    }
  }

  /**
   * API へ載せるアクセストークン。失効が近くリフレッシュトークンがあれば取り直す。
   * 取り直せなければ null を返し、呼び出し側（urql）が 401 と同じ扱いにする。
   */
  accessTokenForRequest(): Promise<string | null> {
    if (this.#refreshing) return this.#refreshing;
    const request = this.#accessTokenForRequest();
    this.#refreshing = request;
    void request.finally(() => {
      if (this.#refreshing === request) this.#refreshing = null;
    });
    return request;
  }

  async #accessTokenForRequest(): Promise<string | null> {
    const epoch = this.#epoch;
    if (this.#state.status !== "authenticated") {
      return null;
    }
    if (!this.tokens.isExpired(this.#now())) {
      return this.tokens.accessToken();
    }
    const refreshToken = this.tokens.refreshToken();
    if (refreshToken === null) {
      this.handleUnauthenticated();
      return null;
    }
    try {
      const endpoints = await this.#resolveEndpoints();
      const tokens = await this.#withClientId((clientId) =>
        refreshAccessToken({
          tokenEndpoint: endpoints.tokenEndpoint,
          clientId,
          refreshToken,
          resource: this.#config.resource,
          fetchImpl: this.#fetch,
          now: this.#now(),
        }),
      );
      if (epoch !== this.#epoch) return null;
      this.tokens.set(tokens);
      return tokens.accessToken;
    } catch {
      if (epoch !== this.#epoch) return null;
      this.handleUnauthenticated();
      return null;
    }
  }

  /** トークンを URL/Storage に保存せず、認可サーバーの HttpOnly セッションへ渡す。 */
  async startDelegatedBrowserSession(input: {
    accessToken: string;
    expiresIn: number;
  }): Promise<void> {
    if (!this.#config.silentAuth) {
      this.adoptDelegatedToken(input);
      return;
    }
    const epoch = ++this.#epoch;
    this.#forgetBrowserHint();
    safeSessionStorage()?.removeItem(HAD_SESSION_KEY);
    safeSessionStorage()?.removeItem(TENANT_HINT_KEY);
    this.tokens.clear();
    this.#set({ status: "authorizing", error: null });
    try {
      const delegationId = delegatedId(input.accessToken);
      if (!delegationId)
        throw new Error("代行トークンの形式が正しくありません。");
      const endpoints = await this.#resolveEndpoints();
      const clientId = await this.#resolveClientId();
      const request = await createAuthorizationRequest({
        endpoints,
        clientId,
        scope: this.#config.scope,
        redirectUri: this.#redirectUri(),
        resource: this.#config.resource,
        returnTo: "/",
        tenantId: tenantHint(input.accessToken),
        tenantParam: this.#config.tenantParam,
      });
      if (epoch !== this.#epoch) return;
      savePendingAuthorization({
        ...request.pending,
        expectedDelegationId: delegationId,
      });
      this.#submitForm(`${this.#config.issuer}/authorize/delegation`, {
        ...Object.fromEntries(new URL(request.url).searchParams),
        delegation_token: input.accessToken,
      });
    } catch (error) {
      if (epoch !== this.#epoch) return;
      clearPendingAuthorization();
      this.#set({ status: "error", error: describe(error) });
      throw error;
    }
  }

  /**
   * CS 代行の委任トークンを受け取る(オペレーター向けアプリからの受け渡し)。
   *
   * **リフレッシュトークンを持たない。** 期限が来たら取り直す経路が無く、
   * `accessTokenForRequest` が `handleUnauthenticated` を通ってログインへ戻る
   * (代行は 60 分で必ず終わる)。
   *
   * Cookie 復元を使わない開発環境向けの受け取り。
   * 本番の受け渡しは startDelegatedBrowserSession で HttpOnly セッションを作る。
   */
  adoptDelegatedToken(input: { accessToken: string; expiresIn: number }): void {
    ++this.#epoch;
    this.#delegated = true;
    this.#forgetBrowserHint();
    safeSessionStorage()?.removeItem(HAD_SESSION_KEY);
    safeSessionStorage()?.removeItem(TENANT_HINT_KEY);
    clearPendingAuthorization();
    this.tokens.set({
      accessToken: input.accessToken,
      tokenType: "Bearer",
      refreshToken: null,
      expiresAt: this.#now() + input.expiresIn * 1000,
    });
    this.#set({
      status: "authenticated",
      error: null,
      requestedTenantId: null,
    });
  }

  /** 401 / UNAUTHENTICATED を受けたとき。持っているトークンを捨ててログインへ戻す */
  handleUnauthenticated(): void {
    ++this.#epoch;
    this.#forgetBrowserHint();
    safeSessionStorage()?.removeItem(HAD_SESSION_KEY);
    safeSessionStorage()?.removeItem(TENANT_HINT_KEY);
    this.tokens.clear();
    clearPendingAuthorization();
    this.#set({ status: "unauthenticated", error: null });
  }

  logout(): void {
    ++this.#epoch;
    const clientId =
      this.#config.clientId ??
      this.#clientId ??
      readCachedClientId(this.#registrationCacheKey());
    const revokeBrowserSession = this.#config.silentAuth && clientId !== null;
    this.tokens.clear();
    clearPendingAuthorization();
    this.#forgetBrowserHint();
    safeSessionStorage()?.removeItem(RESTORE_ATTEMPTED_KEY);
    safeSessionStorage()?.removeItem(HAD_SESSION_KEY);
    safeSessionStorage()?.removeItem(TENANT_HINT_KEY);
    this.#endpoints = null;
    // client_id の登録は消さない。秘密ではないし、ログアウトのたびに
    // 登録し直すと認可サーバーに使い捨ての登録が溜まる
    this.#set({
      status: "unauthenticated",
      error: null,
      requestedTenantId: null,
    });
    if (revokeBrowserSession) {
      this.#submitForm(`${this.#config.issuer}/logout`, {
        client_id: clientId,
        scope: this.#config.scope,
        post_logout_redirect_uri: this.#redirectUri(),
      });
    }
  }
}
